import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Remembers the window scroll position per URL and puts him back ON BACK ONLY (Back or Forward, the
// app's own Back button or the browser's / phone's). Session-scoped (sessionStorage) — clears when the tab closes.
//
// HIS RULE, 2026-10-17: opening a page afresh (a menu tap, a link, a new filter) STARTS AT THE TOP —
// see components/StartAtTop.jsx. Only Back or Forward returns him to the row he was on.
//
// WHY BACK BROKE ON VEHICLES (found 28 Oct): once the browser's own restoring was switched off (17 Oct), the
// list's ONE try at putting him back ran before its rows had arrived — the page was still short, the browser
// clamped the jump to the top, and nothing tried again. Now the jump is RETRIED while the page fills in, and stops
// the moment he touches, scrolls or types — so it can never fight him.
const keyOf = (loc) => "scroll:" + loc.pathname + loc.search;
const TRY_FOR_MS = 12000;
// ONCE HE IS ON HIS ROW IT IS KEPT THERE while the rest of the page fills in (found 4 Nov: What's missing on a busy
// server — the list came back first, Back put him on his row, then the KPI panel ABOVE it answered and pushed the row
// 1,186 px down). Kept for up to 30 s, and let go the moment he touches, scrolls or types.
const KEEP_FOR_MS = 30000;
const HANDS_ON = ["wheel", "touchstart", "keydown", "mousedown"];
// WHERE BACK MUST PUT HIM, held until he is there or takes over himself. While it is held, the half-way
// positions of a page still filling in are NEVER saved as his place — a slow list can't lose it.
let pending = null;
function release() {
  pending = null;
  HANDS_ON.forEach((e) => window.removeEventListener(e, release));
}
function hold(key, y) {
  release();
  pending = { key, y };
  HANDS_ON.forEach((e) => window.addEventListener(e, release, { passive: true }));
}

function restoreTo(key, y) {
  if (pending?.key !== key) hold(key, y);
  const mine = pending;
  const want = openedRow(key);
  let stop = false;
  let flashed = false;
  const t0 = Date.now();
  let lastH = 0;
  let steady = 0;
  const tick = () => {
    if (stop || pending !== mine) return;          // he took over, or another page was opened
    // THE ROW HE OPENED, found by its name — fresh rows may have moved, so it is put back where it sat on screen
    const el = want && visibleNamed(want.id)[0];
    if (el) mine.y = Math.max(0, Math.round(window.scrollY + el.getBoundingClientRect().top - want.top));
    window.scrollTo(0, mine.y);
    // held until the page stops growing (panels above a list can fill in after it), so he lands on the same row;
    // a fresh list may be shorter than it was — then he lands as close as the page allows
    const h = document.documentElement.scrollHeight;
    steady = h === lastH ? steady + 1 : 0;
    lastH = h;
    // the row found but the fresh page too short to put it at the same height — as close as the page allows
    const there = Math.abs(window.scrollY - mine.y) <= 2 || (el && window.scrollY + window.innerHeight >= h - 2);
    const landed = there && steady >= 8;
    if (landed && el && !flashed) flash(el);
    if (landed && !want) { release(); return; }
    if (Date.now() - t0 > (flashed ? KEEP_FOR_MS : TRY_FOR_MS)) { if (el && !flashed) flash(el); release(); return; }
    setTimeout(tick, landed ? 150 : 60);
  };
  const flash = (el) => {
    flashed = true;
    el.setAttribute("data-back-landed", "1");
    el.classList.remove("back-landed");
    void el.offsetWidth;                           // restart the glow if it is still running
    el.classList.add("back-landed");
    setTimeout(() => el.classList.remove("back-landed"), 2400);
  };
  tick();
  return () => { stop = true; };
}

// THE ROW HE OPENED (his rule, 29 Oct): Back lands on the very truck or part he opened — at the same place on screen,
// briefly highlighted. A row says it is one with data-back-row; any other list is read the same way from its names.
const openedKey = (key) => "opened:" + key;
let tapped = null;
function openedRow(key) {
  try { return JSON.parse(sessionStorage.getItem(openedKey(key)) || "null"); } catch { return null; }
}
function visibleNamed(id) {
  return [...document.querySelectorAll(`[data-testid="${CSS.escape(id)}"]`)].filter((e) => e.getClientRects().length > 0);
}
// never a row: the menu, the header, a dialog, a tab strip, anything pinned to the screen
const NOT_A_LIST = "nav, header, aside, [role='dialog'], [role='alertdialog'], [role='menu'], [role='listbox'], [role='tablist'], [data-radix-popper-content-wrapper]";
function pinned(n) {
  for (; n && n !== document.body; n = n.parentElement) {
    const p = getComputedStyle(n).position;
    if (p === "fixed" || p === "sticky") return true;
  }
  return false;
}
function rowOf(target) {
  if (target.closest?.(NOT_A_LIST)) return null;
  const marked = target.closest?.("[data-back-row]");
  if (marked?.dataset.testid) return marked;
  // the outermost named element that is row-sized, the only one of its name on screen, beside rows like it
  const maxH = Math.max(260, window.innerHeight * 0.45);
  let best = null;
  for (let n = target; n && n !== document.body; n = n.parentElement) {
    const id = n.getAttribute?.("data-testid");
    if (!id) continue;
    const h = n.getBoundingClientRect().height;
    if (h > maxH) break;
    const stem = id.replace(/-[^-]+$/, "");
    const like = [...(n.parentElement?.children || [])].filter((c) => c !== n && c.tagName === n.tagName
      && (c.getAttribute("data-testid") || "").startsWith(stem + "-"));
    if (stem !== id && h >= 40 && like.length > 0 && visibleNamed(id).length === 1) best = n;
  }
  return best && !pinned(best) ? best : null;
}
function onTap(e) {
  const row = e.target instanceof Element ? rowOf(e.target) : null;
  tapped = row ? { key: keyOf(window.location), id: row.dataset.testid,
                   top: Math.round(row.getBoundingClientRect().top), at: Date.now() } : null;
}
// leaving a page: the row tapped just before is the one he opened; leaving any other way, there is none
let lastLeft = null;
function leaving(key) {
  lastLeft = null;
  if (tapped?.key === key && Date.now() - tapped.at < 3000) {
    sessionStorage.setItem(openedKey(key), JSON.stringify({ id: tapped.id, top: tapped.top }));
    lastLeft = key;
  } else {
    sessionStorage.removeItem(openedKey(key));
  }
  tapped = null;
}
const savedPlace = (key) => {
  const y = sessionStorage.getItem(key);
  return y != null || openedRow(key) ? parseInt(y || "0", 10) : null;
};

// EVERY PAGE — mounted once, in StartAtTop. Saves his place on every page, and on Back puts him there.
export function useEveryPlaceKept() {
  const location = useLocation();
  const navType = useNavigationType();
  const key = keyOf(location);
  const cancel = useRef(null);

  // The place is always SAVED, whichever way he arrived — so Back works from anywhere.
  useEffect(() => {
    let frame = 0;
    const here = location.pathname + location.search;
    const stillHere = () => window.location.pathname + window.location.search === here;
    const onScroll = () => {
      if (frame || !stillHere()) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        // leaving the list fires one last scroll event against THIS list's key; a write only counts
        // while he is still standing on this page, so his place is never overwritten on the way out
        if (stillHere() && pending?.key !== key) sessionStorage.setItem(key, String(window.scrollY));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
      leaving(key);
    };
  }, [key, location.pathname, location.search]);

  useEffect(() => {
    document.addEventListener("click", onTap, true);
    return () => document.removeEventListener("click", onTap, true);
  }, []);

  useEffect(() => {
    // REPLACE is a page writing its own filters into the URL a beat after he lands — keep putting him back
    // (a tap that only rewrote this page's own URL opened nothing)
    if (navType === "REPLACE") {
      if (lastLeft) sessionStorage.removeItem(openedKey(lastLeft));
      if (pending) pending.key = key;
      return;
    }
    cancel.current?.();
    cancel.current = null;
    if (navType !== "POP") return;
    const y = savedPlace(key);
    if (y != null) cancel.current = restoreTo(key, y);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navType]);

  useEffect(() => () => cancel.current?.(), []);
}

// A LIST — pass `ready` = the list has rendered its rows, so the jump is tried again once they are in.
export function useScrollMemory(ready = true) {
  const location = useLocation();
  const navType = useNavigationType();
  const key = keyOf(location);
  const restored = useRef(false);
  const cancel = useRef(null);
  // HOW HE ARRIVED, remembered. A list restores its own filters into the URL a beat after he lands,
  // which reads as a REPLACE — if that counted as a fresh open, Back would lose his place again.
  const arrival = useRef(navType);

  useEffect(() => { if (navType !== "REPLACE") arrival.current = navType; }, [navType]);

  useEffect(() => { restored.current = false; }, [key]);

  useEffect(() => {
    if (!ready || restored.current) return;
    restored.current = true;
    if (arrival.current !== "POP") return;    // a fresh open starts at the top
    const y = pending?.key === key ? pending.y : savedPlace(key);
    if (y != null) cancel.current = restoreTo(key, y);
  }, [ready, key, navType]);

  useEffect(() => () => cancel.current?.(), []);
}

// WHAT HE HAD PICKED — a search box, a filter, a tab, a sort, a page — kept for Back like his place is.
// Like useState, but arriving by Back gives him what he had; a fresh open starts from `initial`.
export function useBackState(name, initial) {
  const location = useLocation();
  const navType = useNavigationType();
  const key = `back:${location.pathname}:${name}`;
  const [v, setV] = useState(() => {
    if (navType === "POP") {
      const s = sessionStorage.getItem(key);
      if (s != null) { try { return JSON.parse(s); } catch { /* an old copy — start fresh */ } }
    }
    return typeof initial === "function" ? initial() : initial;
  });
  useEffect(() => { sessionStorage.setItem(key, JSON.stringify(v ?? null)); }, [key, v]);
  return [v, setV];
}
